import * as classNames from 'classnames'
import * as React from 'react'
import * as moment from 'moment'
import { Link } from 'react-router-dom'
import { useTable, usePagination, useSortBy, Column } from 'react-table'
import { IRiderFilters, IRiderSearchObject, IMembershipRiderObject } from '../../types'
import Edit from '../icons/ui/Edit'
import Archive from '../icons/drivers/Archive'
import RestoreFromArchive from '../icons/drivers/RestoreFromArchive'
import ReactTablePagination from '../ui/ReactTablePagination'
import ContextMenu from '../ui/ContextMenu'
import UIMessages from '../../utils/ui-messages'
import { MoonLoader } from 'react-spinners'

export interface ITableRidersProps {
  filters: IRiderFilters
  riders: IRiderSearchObject[]
  totalItems: number
  deactivatedRiderId: string
  activate: (rider: IRiderSearchObject) => void
  deactivate: (rider: IRiderSearchObject) => void
  fetchRiders: (filters: IRiderFilters) => Promise<any>
  moveToArchive: (rider: IRiderSearchObject) => void
  moveToActive: (rider: IRiderSearchObject) => void
}

export interface ITableRidersState {
  pages: number
}

interface IProps<T extends object> {
  columns: Array<Column<T>>
  data: T[]
  loading: boolean
  filters: IRiderFilters
  pageCount: number
  fetchData: (params: { filters: IRiderFilters; pageIndex: number; pageSize: number }) => void
  pageIndex: number
  setPageIndex: (size: number) => any
}

const PAGE_SIZE = 50

function Table<T extends object>({
  columns,
  data,
  fetchData,
  loading,
  filters,
  pageCount: controlledPageCount,
  pageIndex,
  setPageIndex
}: IProps<T>) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setPageSize, state } = useTable<T>(
    {
      columns,
      data,
      initialState: {
        pageSize: PAGE_SIZE
      },
      manualSortBy: true,
      manualPagination: true,
      pageCount: controlledPageCount
    },
    useSortBy,
    usePagination
  )

  const { pageSize, sortBy } = state
  const [canPreviousPage, setCanPreviousPage] = React.useState(false)
  const [canNextPage, setCanNextPage] = React.useState(false)

  React.useEffect(() => {
    const sortParams = sortBy.map(s => {
      return s.id + '-' + (s.desc ? 'desc' : 'asc')
    })

    if (sortParams.length === 0) {
      sortParams.push('name-asc')
    }
    fetchData({ filters: { ...filters, sortBy: sortParams }, pageIndex, pageSize })
  }, [filters, pageIndex])

  React.useEffect(() => {
    const sortParams = sortBy.map(s => {
      return s.id + '-' + (s.desc ? 'desc' : 'asc')
    })

    if (sortParams.length === 0) {
      sortParams.push('name-asc')
    }
    fetchData({ filters: { ...filters, sortBy: sortParams }, pageIndex, pageSize })
  }, [sortBy])

  React.useEffect(() => {
    setPageIndex(0)
    const resettedPageIndex: number = 0
    const sortParams = sortBy.map(s => {
      return s.id + '-' + (s.desc ? 'desc' : 'asc')
    })

    if (sortParams.length === 0) {
      sortParams.push('name-asc')
    }
    fetchData({ filters: { ...filters, sortBy: sortParams }, pageIndex: resettedPageIndex, pageSize })
  }, [pageSize])

  React.useEffect(() => {
    if (pageIndex > 0) {
      setCanPreviousPage(true)
    } else {
      setCanPreviousPage(false)
    }

    if (pageIndex === controlledPageCount - 1) {
      setCanNextPage(false)
    } else {
      setCanNextPage(true)
    }
  }, [pageIndex, pageSize, controlledPageCount])

  const nextPage = () => {
    setPageIndex(pageIndex + 1)
  }

  const previousPage = () => {
    setPageIndex(pageIndex - 1)
  }

  return (
    <React.Fragment>
      {loading ? (
        <div className='flex flex-column justify-center items-center' style={{ width: '90%', height: '40vh' }}>
          <MoonLoader sizeUnit={'px'} size={40} color={'#405264'} loading={true} />
          <span className='pt1'>Loading...</span>
        </div>
      ) : (
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th
                    className={classNames('', {
                      sortable: column.canSort,
                      sortedAsc: column.isSortedDesc === false,
                      sortedDesc: column.isSortedDesc === true
                    })}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {loading ? (
              <tr>
                <td>Loading...</td>
              </tr>
            ) : (
              rows.map(
                (row, i) =>
                  +prepareRow(row) || (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell, index) => {
                        return (
                          <td {...cell.getCellProps()} key={`1-${index}`}>
                            {cell.render('Cell')}
                          </td>
                        )
                      })}
                    </tr>
                  )
              )
            )}
          </tbody>
        </table>
      )}
      <ReactTablePagination
        page={pageIndex}
        pages={controlledPageCount}
        pageSize={pageSize}
        canNext={canNextPage}
        canPrevious={canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
        rowCount={rows.length}
      />
    </React.Fragment>
  )
}

const CellBasicInfo = (props: { id: string; firstName: string; lastName: string }) => (
  <div className='flex items-center'>
    <Link to={`/riders/${props.id}/profile/info`} className='ml2' style={{ whiteSpace: 'normal' }}>
      {props.firstName} {props.lastName}
    </Link>
  </div>
)

const buildMembershipCard = (membership: IMembershipRiderObject) => {
  const serviceName = membership && membership.service && membership.service.name ? membership.service.name : ''
  const externalId = membership && membership.externalRiderId ? `, ${membership.externalRiderId}` : ''
  return (
    <div className='m1 rounded border-grey4 bg-white font-grey3'>
      <div className='h6 bold ml2 mr2 mt02 mb02' style={{ wordBreak: 'break-all' }}>
        {serviceName}
        {externalId}
      </div>
    </div>
  )
}

const renderEmail = (rider: IRiderSearchObject) => {
  if (!rider || !rider.email) {
    return ''
  }

  return rider.email
}

const renderUsername = (rider: IRiderSearchObject) => {
  if (!rider || !rider.username) {
    return ''
  }

  return rider.username
}

const renderCreatedDate = (rider: IRiderSearchObject) => {
  if (!rider || !rider.createdAt) {
    return ''
  }
  return moment(rider.createdAt).format('MMM DD YYYY')
}

export default function RidersTable(props: ITableRidersProps) {
  const [loading, setLoading] = React.useState(false)
  const [pageSize, setPageSize] = React.useState(PAGE_SIZE)
  const [pageIndex, setPageIndex] = React.useState(0)

  const columns = React.useMemo(
    () => [
      {
        id: 'name',
        Header: 'Name',
        width: 200,
        accessor: (rider: IRiderSearchObject) => {
          return <Link to={`/riders/${rider.id}/profile/info`}>{rider.name}</Link>
        }
      },
      {
        id: 'phone',
        Header: 'Phone Number',
        accessor: (rider: IRiderSearchObject) => {
          let phoneNumber = ''
          if (rider.phone ? rider.phone.indexOf(rider.countryCode) === 0 : false) {
            phoneNumber = rider.phone ? rider.phone.replace(rider.countryCode, '') : ''
          } else {
            phoneNumber = rider.phone ? rider.phone.replace('+', '') : ''
          }

          return phoneNumber !== '' && phoneNumber !== '1' ? '+' + phoneNumber : 'none'
        },
        disableSortBy: true,
        className: 'flex items-center'
      },
      {
        id: 'email',
        Header: 'Email',
        accessor: (rider: IRiderSearchObject) => renderEmail(rider),
        className: 'RidersListItemEmail flex items-center'
      },
      {
        id: 'username',
        Header: 'Username',
        accessor: (rider: IRiderSearchObject) => renderUsername(rider),
        className: 'RidersListItemEmail flex items-center'
      },
      {
        id: 'memberships',
        Header: 'Memberships',
        accessor: 'membership',
        className: 'flex items-center',
        disableSortBy: true,
        Cell: (data: any) => {
          const rider = data.row.original
          if (!rider.serviceMemberships || !rider.serviceMemberships.length) {
            return ''
          }
          let temp = ''

          rider.serviceMemberships.map((v: IMembershipRiderObject, key: number) => {
            temp = v.status === 'active' || v.status === 'ACCEPTED' ? temp + v.service.name + ', ' : temp + ''
          })

          return temp.slice(0, -2)
        }
      },
      {
        id: 'createdAt',
        Header: 'Created Date',
        accessor: (rider: IRiderSearchObject) => renderCreatedDate(rider),
        className: 'flex items-center'
      },
      {
        id: 'status',
        Header: '',
        accessor: 'status',
        className: 'overflow--visible',
        disableSortBy: true,
        Cell: (data: any) => {
          return (
            <CellContextMenu
              rider={data.row.original}
              isArchived={props.filters.isArchived || false}
              isActive={props.filters.isActive || false}
              activate={activateRider}
              deactivate={deactivateRider}
              moveToArchive={archiveRider}
              moveToActive={unarchiveRider}
            />
          )
        }
      }
    ],
    []
  )

  const CellContextMenu = (props: {
    rider: IRiderSearchObject
    isArchived: boolean
    isActive: boolean
    moveToArchive: (item: IRiderSearchObject) => () => void
    moveToActive: (item: IRiderSearchObject) => () => void
    activate: (item: IRiderSearchObject) => () => void
    deactivate: (item: IRiderSearchObject) => () => void
  }) => {
    const archiveText = props.rider.isArchived ? 'Restore' : 'Move to archive'
    const archiveIcon = props.rider.isArchived ? (
      <RestoreFromArchive className='icon--16x16 cursor--pointer mr1' />
    ) : (
      <Archive className='icon--16x16 cursor--pointer mr1' />
    )
    return (
      <div className='flex items-center'>
        <ContextMenu>
          {props.rider.isArchived && (
            <div className='MenuItem flex cursor--pointer p02'>
              <div className='onMoveToArchiveOption' onClick={props.moveToActive(props.rider)}>
                {archiveIcon}
                <span className='font-dark-grey h5'>{archiveText}</span>
              </div>
            </div>
          )}

          {!props.rider.isArchived && (
            <div className='MenuItem flex cursor--pointer p02'>
              <div className='onMoveToArchiveOption' onClick={props.moveToArchive(props.rider)}>
                {archiveIcon}
                <span className='font-dark-grey h5'>{archiveText}</span>
              </div>
            </div>
          )}

          {!props.rider.isArchived && (
            <div className='MenuItem flex cursor--pointer p02'>
              <Link to={`/riders/${props.rider.id}/edit/profile`}>
                <Edit className='icon--small svg-stroke-grey3 mr1' />
                <span className='font-dark-grey h5'>{UIMessages.common.edit}</span>
              </Link>
            </div>
          )}

          {!props.rider.isArchived && (
            <div className='MenuItem flex cursor--pointer p02'>
              <Link to={`/trips/recent/${props.rider.id}`}>
                <Edit className='icon--small svg-stroke-grey3 mr1' />
                <span className='font-dark-grey h5'>{UIMessages.common.NewRequest}</span>
              </Link>
            </div>
          )}
        </ContextMenu>
      </div>
    )
  }

  const archiveRider = (item: IRiderSearchObject) => () => {
    return props.moveToArchive(item)
  }

  const unarchiveRider = (item: IRiderSearchObject) => () => {
    return props.moveToActive(item)
  }

  const activateRider = (item: IRiderSearchObject) => () => {
    return props.activate(item)
  }

  const deactivateRider = (item: IRiderSearchObject) => () => {
    return props.deactivate(item)
  }

  const fetchData = React.useCallback(async ({ filters, pageSize, pageIndex }) => {
    setLoading(true)
    let sortBy

    if (filters.sortBy.length > 0) {
      sortBy = filters.sortBy
    } else {
      sortBy = ['name-asc']
    }
    await props.fetchRiders({ ...filters, skip: pageIndex * pageSize, take: pageSize, sortBy })
    setLoading(false)
    setPageSize(pageSize)
  }, [props.filters, pageSize, pageIndex])

  let tableData = props.riders
  let tablePageCount = props.totalItems

  if (props.deactivatedRiderId !== null && props.deactivatedRiderId !== undefined && props.deactivatedRiderId !== '') {
    const updatedRidersList = props.riders.filter(r => r.id !== props.deactivatedRiderId)
    tableData = updatedRidersList
    const updatedTotalItems = updatedRidersList.length
    tablePageCount = updatedTotalItems
  }

  const pageCount = Math.ceil(tablePageCount / pageSize)

  return (
    <React.Fragment>
      <div className='react-table'>
        <Table
          columns={columns}
          data={tableData}
          fetchData={fetchData}
          filters={props.filters}
          loading={loading}
          pageCount={pageCount}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
        />
      </div>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./src/components/riders/RidersTable.tsx